// Generic hook for paginated data fetching with infinite scroll
// CREATED: Provides server-side pagination with Supabase
// CHANGE: Removed all debug console statements for production
// Features:
// - Configurable page size (default 20 items)
// - Automatic loading state management
// - Refresh capability
// - hasMore indicator for infinite scroll
// - Error handling
// - Multi-column search support (single string or array of column names)
// FIXED: Added orderBy to filterKey dependencies to trigger reload when sorting changes
// UPDATED: Added RPC function support for complex queries (e.g., many-to-many relationships)
// - Use rpcFunction + rpcParams to call a PostgreSQL function instead of querying a table
// - Maintains backward compatibility with table-based queries
// CHANGE: Added fetchId pattern to prevent stale async responses from overwriting current state on rapid filter changes

import { useState, useEffect, useCallback, useRef } from 'react';
import { SupabaseClient } from '@supabase/supabase-js';

interface UsePaginatedDataOptions<T> {
  supabase: SupabaseClient;
  table?: string;
  rpcFunction?: string;
  rpcParams?: Record<string, unknown>;
  pageSize?: number;
  orderBy?: { column: string; ascending?: boolean };
  filters?: Record<string, unknown>;
  searchColumn?: string | string[];
  searchTerm?: string;
  select?: string;
  enabled?: boolean;
}

interface UsePaginatedDataResult<T> {
  items: T[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  totalCount: number | null;
}

export function usePaginatedData<T>({
  supabase,
  table,
  rpcFunction,
  rpcParams = {},
  pageSize = 20,
  orderBy = { column: 'created_at', ascending: false },
  filters = {},
  searchColumn,
  searchTerm,
  select = '*',
  enabled = true,
}: UsePaginatedDataOptions<T>): UsePaginatedDataResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const currentOffset = useRef(0);
  const fetchIdRef = useRef(0);

  // Validate that either table or rpcFunction is provided
  if (!table && !rpcFunction) {
    throw new Error('usePaginatedData: Either table or rpcFunction must be provided');
  }

  // Create a stable key for filters, search, orderBy, and RPC params
  const filterKey = JSON.stringify({
    filters,
    searchTerm,
    searchColumn,
    orderBy,
    rpcParams,
    rpcFunction
  });

  const fetchData = useCallback(
    async (offset: number, append: boolean = false) => {
      if (!enabled) return;

      const fetchId = ++fetchIdRef.current;

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        let newItems: T[] = [];
        let count: number | null = null;

        if (rpcFunction) {
          const params = {
            ...rpcParams,
            p_order_by: orderBy.column,
            p_order_asc: orderBy.ascending ?? false,
            p_limit: pageSize,
            p_offset: offset,
          };

          if (searchTerm && searchTerm.trim() !== '') {
            params.p_search_term = searchTerm.trim();
          }

          const { data, error: fetchError } = await supabase.rpc(rpcFunction, params);

          if (fetchError) throw fetchError;

          if (data && typeof data === 'object') {
            newItems = (data.users || []) as T[];
            count = data.total_count || 0;
          }
        } else if (table) {
          let query = supabase
            .from(table)
            .select(select, { count: 'exact' })
            .range(offset, offset + pageSize - 1);

          Object.entries(filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              if (Array.isArray(value)) {
                if (value.length > 0) {
                  query = query.in(key, value);
                }
              } else {
                query = query.eq(key, value);
              }
            }
          });

          if (searchColumn && searchTerm && searchTerm.trim() !== '') {
            if (Array.isArray(searchColumn)) {
              const orConditions = searchColumn
                .map(col => `${col}.ilike.*${searchTerm.trim()}*`)
                .join(',');
              query = query.or(orConditions);
            } else {
              query = query.ilike(searchColumn, `%${searchTerm.trim()}%`);
            }
          }

          query = query.order(orderBy.column, { ascending: orderBy.ascending ?? false });

          const { data, error: fetchError, count: queryCount } = await query;

          if (fetchError) throw fetchError;

          newItems = (data as T[]) || [];
          count = queryCount;
        }

        if (fetchId !== fetchIdRef.current) return;

        if (append) {
          setItems((prev) => [...prev, ...newItems]);
        } else {
          setItems(newItems);
        }

        setTotalCount(count);
        setHasMore(newItems.length === pageSize);
        currentOffset.current = append ? offset + pageSize : pageSize;
      } catch (err) {
        if (fetchId !== fetchIdRef.current) return;
        setError(err as Error);
      } finally {
        if (fetchId === fetchIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [enabled, supabase, table, rpcFunction, select, pageSize, orderBy, filterKey]
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || isLoading) return;
    await fetchData(currentOffset.current, true);
  }, [hasMore, isLoadingMore, isLoading, fetchData]);

  const refresh = useCallback(async () => {
    currentOffset.current = 0;
    setHasMore(true);
    await fetchData(0, false);
  }, [fetchData]);

  // Initial load and reload on filter/search changes
  useEffect(() => {
    currentOffset.current = 0;
    setHasMore(true);
    fetchData(0, false);
  }, [filterKey, enabled]);

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
    totalCount,
  };
}
